import React, {
  Children,
  cloneElement,
  FC,
  isValidElement,
  PropsWithChildren,
  ReactElement,
  useCallback,
  useState
} from 'react';
import {
  Box,
  BoxProps,
  CircularProgress,
  CircularProgressProps,
  Collapse,
  Stack,
  StackProps,
  Typography
} from '@mui/material';
import omit from 'lodash/fp/omit';
import { ID, isErrorDetails } from '@ianesthesie-kit/services';
import { CenteredContainer } from '@ianesthesie-kit/core';
import { SentryService } from '@ianesthesie-kit/sentry';
import { useErrorDialog } from '@ianesthesie-kit/redux';
import { SourceType } from '@/modules/surgicalEvent';
import {
  SourceTypeWrapper,
  SourceTypeWrapperProps
} from '@/components/sourceType';

export const DEFAULT_DEBOUNCE_TIMEOUT = 2500;

const getSingleKeyButId = (entity: object) => {
  const keys = Object.keys(entity).filter((k) => k !== 'id');
  return keys.length === 1 && keys[0];
};

const DefaultGroupWrapper = (props: StackProps) => (
  <Stack direction="row" gap={2} {...props} />
);

type LoadingOverlayProps = CircularProgressProps & {
  containerProps?: BoxProps;
};

export const LoadingOverlay = ({
  containerProps,
  ...progressProps
}: LoadingOverlayProps) => (
  <CenteredContainer
    position="absolute"
    top={0}
    right={0}
    bottom={0}
    left={0}
    zIndex={200}
    sx={{ background: 'rgba(255,255,255, 0.4)' }}
    {...containerProps}
  >
    <CircularProgress size={24} {...progressProps} />
  </CenteredContainer>
);

export type BasePatcherProps = {
  name: string;
  error?: string;
  wrapper?: FC<PropsWithChildren<unknown>>;
  loading?: boolean;
  hideLoadingOverlay?: boolean;
  sourceType?: SourceType;
  readOnly?: boolean;
  hidden?: boolean;
};

export type PatchableGroupProps<T> = {
  entity: T;
  children: React.ReactNode | React.ReactNode[];
  onPatch: (partial: Partial<T>) => Promise<unknown>;
  wrapper?: FC<PropsWithChildren<unknown>>;
  childWrapper?: FC<PropsWithChildren<unknown>>;
  readOnly?: boolean;
};

export type WithPassedPatchableGroupProps<T, P> = P & {
  patchable?: {
    entity: T;
    currentValue: any;
    readOnly?: boolean;
    onPatch: <TT>(partial: Partial<TT>) => Promise<unknown>;
  };
};

export const PatchableGroup = <T extends object>({
  entity,
  readOnly,
  onPatch,
  wrapper,
  childWrapper,
  children
}: PatchableGroupProps<T>) => {
  const { showErrorDialog } = useErrorDialog();
  const [errors, setErrors] = useState<{ [k in keyof T]?: string }>({});
  const [loadingName, setLoadingName] = useState<undefined | keyof T>();
  const patch = useCallback(
    (child: ReactElement<BasePatcherProps>) => (partial: Partial<T>) => {
      if (child.props.readOnly ?? readOnly) {
        SentryService.getInstance().captureException({
          message: 'Patchable group: patch called while in readOnly mode',
          patchKeys: Object.keys(partial)
        });
        return Promise.reject(new Error('Patch called while in readOnly mode'));
      }
      setLoadingName(Object.keys(partial)?.[0] as keyof T);
      const partialWithId: Partial<T & { id?: ID }> = partial;
      if ('id' in entity) {
        partialWithId.id = (entity as typeof partialWithId).id;
      }
      return onPatch(partialWithId)
        .then((resp: any) => {
          if (resp?.error) {
            throw resp.error;
          }
          return resp;
        })
        .then(() => {
          setErrors((e) => omit(Object.keys(partial), e as any));
        })
        .catch((err) => {
          if (isErrorDetails(err)) {
            if (err.status >= 400 && err.status < 500) {
              const key = getSingleKeyButId(partial);
              const errs = err.errors?.reduce(
                (res, cv) => ({
                  ...res,
                  [key || cv.field]: cv.message
                }),
                {}
              );
              setErrors((e) => ({ ...e, ...errs }));
            }
          } else {
            showErrorDialog(err);
            throw err;
          }
        })
        .finally(() => {
          setLoadingName(undefined);
        });
    },
    [entity, onPatch, readOnly, showErrorDialog]
  );

  const Wrapper = wrapper || DefaultGroupWrapper;
  const DefaultChildWrapper = childWrapper || Box;

  return (
    <Wrapper>
      {Children.map(children, (child) => {
        if (!child) {
          return null;
        }
        if (!isValidElement(child)) {
          throw new Error('Not a valid child');
        }
        const childProps = child?.props as BasePatcherProps;
        if (childProps?.hidden) {
          return null;
        }
        const name = childProps.name as keyof T;
        const sourceType = childProps.sourceType as SourceType | undefined;
        const ThisChildWrapper = childProps.wrapper || DefaultChildWrapper;
        if (!name) {
          return (
            <ThisChildWrapper>
              <ConditionalSourceTypeWrapper sourceType={sourceType}>
                {child}
              </ConditionalSourceTypeWrapper>
            </ThisChildWrapper>
          );
        }
        const hideLoadingOverlay = childProps.hideLoadingOverlay;
        const error = errors[name];
        const loading = loadingName === name;
        return (
          <ThisChildWrapper>
            <Box position="relative" height="100%">
              {loading && !hideLoadingOverlay && <LoadingOverlay />}
              <ConditionalSourceTypeWrapper
                styleBorder={false}
                sourceType={childProps.sourceType}
              >
                {cloneElement(child, {
                  error,
                  loading,
                  patchable: {
                    entity,
                    readOnly:
                      childProps.readOnly ??
                      (sourceType === SourceType.EPIC ||
                        sourceType === SourceType.PATIENTS_APP ||
                        readOnly),
                    currentValue: entity[name],
                    onPatch: patch(child as any)
                  }
                } as any)}
              </ConditionalSourceTypeWrapper>
            </Box>
            <Collapse in={!!error}>
              <div>
                <Typography variant="body2" color="error">
                  {error}
                </Typography>
              </div>
            </Collapse>
          </ThisChildWrapper>
        );
      })}
    </Wrapper>
  );
};

function ConditionalSourceTypeWrapper(props: SourceTypeWrapperProps) {
  if (!props.sourceType) {
    return <>{props.children}</>;
  }

  return <SourceTypeWrapper {...props} />;
}
